import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInventorySummary } from '../api';

const emptyState = () => ({ rows: [], loaded: 0, total: null, loading: false, error: '' });

// One request at a time, with breathing room between batches. Refresh events
// during a load are coalesced into one subsequent read.
export default function useInventoryCategory(branchId, categoryId) {
  const owner = useMemo(() => ({ active: true, controller: null, timer: null, run: null,
    refreshPending: false, cursor: null, state: emptyState() }), [branchId, categoryId]);
  const [snapshot, setSnapshot] = useState({ owner, ...owner.state });
  const load = useCallback(async (resume = false) => {
    if (!owner.active || !branchId || !categoryId) return;
    if (owner.run) {
      owner.refreshPending = true;
      return owner.run;
    }
    const publish = changes => {
      owner.state = { ...owner.state, ...changes };
      setSnapshot({ owner, ...owner.state });
    };
    owner.run = (async () => {
      do {
        owner.refreshPending = false;
        if (!resume) { owner.cursor = null; publish(emptyState()); }
        publish({ loading: true, error: '' });
        owner.controller = new AbortController();
        const { signal } = owner.controller;
        try {
          do {
            const response = await getInventorySummary(branchId, {
              categoryId, loadMode: 'category', afterVarietyId: owner.cursor || undefined,
            }, { signal });
            if (!owner.active || signal.aborted) break;
            const batch = response.batch;
            if (!batch || (batch.nextAfterVarietyId && Number(batch.nextAfterVarietyId) <= Number(owner.cursor))) {
              throw new Error('Invalid inventory batch');
            }
            const rowsByKey = new Map(owner.state.rows.map(row => [String(row.inventoryKey || row.varietyId), row]));
            response.data.forEach(row => rowsByKey.set(String(row.inventoryKey || row.varietyId), row));
            const loaded = owner.state.loaded + batch.loaded;
            owner.cursor = batch.nextAfterVarietyId;
            publish({ rows: [...rowsByKey.values()].sort((a, b) =>
              `${a.productName || ''} ${a.varietyName || ''}`.localeCompare(`${b.productName || ''} ${b.varietyName || ''}`)),
            loaded, total: loaded + (owner.cursor ? batch.remaining : 0) });
            if (owner.cursor) await new Promise(resolve => {
              owner.timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 250);
              const cancel = () => { clearTimeout(owner.timer); resolve(); };
              signal.addEventListener('abort', cancel, { once: true });
            });
          } while (owner.cursor && owner.active && !signal.aborted);
        } catch (error) {
          if (owner.active && !signal.aborted) publish({ error: 'No se pudo cargar inventario. Puedes reintentar para continuar.' });
        } finally {
          if (owner.active) publish({ loading: false });
        }
        resume = false;
      } while (owner.active && owner.refreshPending);
    })();
    await owner.run;
    owner.run = null;
  }, [branchId, categoryId, owner]);
  useEffect(() => {
    owner.active = true;
    void load();
    return () => { owner.active = false; owner.controller?.abort(); clearTimeout(owner.timer); };
  }, [load, owner]);
  const state = snapshot.owner === owner ? snapshot : emptyState();
  return { ...state, loading: Boolean(categoryId) && (snapshot.owner !== owner || state.loading),
    refresh: load, retry: () => load(true) };
}
